import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarSync, Copy, ExternalLink, RefreshCw } from "lucide-react";

const feedUrl = (t) => `${process.env.REACT_APP_BACKEND_URL}/api/calendar/feed/${t}.ics`;

export function CalendarSyncButton() {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null);
  const load = async (reset = false) => {
    try {
      const r = reset ? await api.post("/calendar/token/reset") : await api.get("/calendar/token");
      setToken(r.data.token);
      if (reset) toast.success("Nuovo link creato: aggiungilo di nuovo al calendario");
    } catch (e) { toast.error("Link non disponibile"); }
  };
  const openDlg = () => { setOpen(true); if (!token) load(); };
  const url = token ? feedUrl(token) : "";
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copiato"); } catch (e) { toast.error("Copia non riuscita"); } };
  const gcal = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(url.replace(/^https?:/, "webcal:"))}`;
  return (
    <>
      <Button data-testid="cal-sync-btn" onClick={openDlg} variant="outline" size="sm" className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10">
        <CalendarSync className="w-4 h-4 mr-1.5" /> Google Calendar
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg" data-testid="cal-sync-dialog">
          <DialogHeader>
            <DialogTitle>Sincronizza con Google Calendar</DialogTitle>
            <DialogDescription className="text-slate-400">
              Nel tuo calendario compariranno i servizi confermati e le date in cui hai dato disponibilità (come «Da confermare»). Google aggiorna il calendario da solo, di solito entro qualche ora.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input readOnly value={url} data-testid="cal-sync-url" className="bg-slate-950 border-slate-700 text-xs" onFocus={(e) => e.target.select()} />
              <Button data-testid="cal-sync-copy" onClick={copy} disabled={!token} size="sm" className="bg-cyan-500 text-slate-950 hover:bg-cyan-400"><Copy className="w-4 h-4" /></Button>
            </div>
            <a data-testid="cal-sync-open-google" href={gcal} target="_blank" rel="noreferrer"
              className={`flex items-center justify-center gap-2 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold py-2 transition-colors ${token ? "" : "pointer-events-none opacity-50"}`}>
              <ExternalLink className="w-4 h-4" /> Aggiungi a Google Calendar
            </a>
            <ol className="text-xs text-slate-400 list-decimal pl-4 space-y-1">
              <li>Da computer premi «Aggiungi a Google Calendar» e conferma con «Aggiungi».</li>
              <li>In alternativa: Google Calendar → Altri calendari → «+» → Da URL, incolla il link.</li>
              <li>Su iPhone puoi aprire il link direttamente: si aggiunge al calendario del telefono.</li>
            </ol>
            <p className="text-xs text-amber-300/80">Il link è personale: non condividerlo. Se l'hai condiviso per sbaglio, creane uno nuovo.</p>
            <Button data-testid="cal-sync-reset" onClick={() => load(true)} variant="ghost" size="sm" className="text-slate-400 hover:text-slate-200">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Crea un nuovo link
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
